import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { getSecurityHeaders, securityConfig, logSecurityEvent } from '@/lib/security';

interface SecurityContextType {
  config: typeof securityConfig;
  logSecurityEvent: (event: string, details?: Record<string, any>) => void;
}

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

interface SecurityProviderProps {
  children: ReactNode;
}

export function SecurityProvider({ children }: SecurityProviderProps) {
  // Apply security headers to the document
  useEffect(() => {
    const headers = getSecurityHeaders(securityConfig);

    // Apply CSP and other security headers via meta tags
    Object.entries(headers).forEach(([name, value]) => {
      if (name === 'Content-Security-Policy') {
        // Set CSP via meta tag
        let meta = document.querySelector('meta[http-equiv="Content-Security-Policy"]') as HTMLMetaElement;
        if (!meta) {
          meta = document.createElement('meta');
          meta.httpEquiv = 'Content-Security-Policy';
          document.head.appendChild(meta);
        }
        // frame-ancestors is ignored in meta; strip it to avoid warnings
        const sanitized = value.replace(/(?:^|;\s*)frame-ancestors\s+[^;]+;?/i, '; ').replace(/;\s*;+/g, '; ').trim();
        meta.content = sanitized;
      } else {
        // Set other headers as meta tags
        let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
        if (!meta) {
          meta = document.createElement('meta');
          meta.name = name;
          document.head.appendChild(meta);
        }
        meta.content = value;
      }
    });

    // Log application startup for security monitoring
    logSecurityEvent('app_started', {
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    });
  }, []);

  const value: SecurityContextType = {
    config: securityConfig,
    logSecurityEvent,
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
}

export function useSecurity(): SecurityContextType {
  const context = useContext(SecurityContext);
  if (context === undefined) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return context;
}
